import type { ChecklistItemDto, TestCaseRefDto } from '@tms/shared';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Copy,
  Link2,
  Link2Off,
  Pencil,
  Plus,
  Save,
  Trash2,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { PageHeader } from '../../components/layout/PageHeader';
import { RichText, RichTextEditor, isBlank } from '../../components/rich-text';
import {
  Button,
  buttonClassName,
  Card,
  CardBody,
  CardHeader,
  CaseStatusBadge,
  CollapsibleCard,
  ConfirmDialog,
  CopyKey,
  EntityKey,
  ErrorState,
  LabelChip,
  SkeletonRows,
  Table,
  TableWrap,
  Td,
  Th,
} from '../../components/ui';
import { useErrorText, useFormat, useT } from '../../i18n';
import { usePermissions } from '../auth/permissions';
import { ChecklistFormDialog } from './ChecklistFormDialog';
import { LinkCaseDialog } from './LinkCaseDialog';
import {
  useChecklist,
  useDeleteChecklist,
  useDuplicateChecklist,
  useReplaceChecklistItems,
} from './api';

interface DraftItem {
  title: string;
  expectedResult: string;
  testCase: TestCaseRefDto | null;
}

const toDraft = (items: ChecklistItemDto[]): DraftItem[] =>
  items.map((item) => ({
    title: item.title,
    expectedResult: item.expectedResult ?? '',
    testCase: item.testCase,
  }));

export const ChecklistDetailPage = ({
  projectCode,
  checklistKey,
}: {
  projectCode: string;
  checklistKey: string;
}) => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { t } = useT();
  const { dateTime } = useFormat();
  const { errorText } = useErrorText();
  const { data: checklist, isPending, error, refetch } = useChecklist(projectCode, checklistKey);

  const [draft, setDraft] = useState<DraftItem[]>([]);
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [linkingIndex, setLinkingIndex] = useState<number | null>(null);

  const replaceItems = useReplaceChecklistItems(projectCode, checklist?.id ?? '');
  const duplicate = useDuplicateChecklist(projectCode);
  const remove = useDeleteChecklist(projectCode);

  // Reset the editor whenever a different (or reloaded) checklist arrives.
  useEffect(() => {
    if (!checklist) return;
    setDraft(toDraft(checklist.items));
    setDirty(false);
  }, [checklist]);

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (isPending || !checklist) return <SkeletonRows rows={5} className="p-0" />;

  const editable = can('checklist:write');

  const change = (next: DraftItem[]) => {
    setDraft(next);
    setDirty(true);
  };

  const update = (index: number, patch: Partial<DraftItem>) => {
    change(draft.map((item, position) => (position === index ? { ...item, ...patch } : item)));
  };

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= draft.length) return;
    const next = [...draft];
    const [moved] = next.splice(index, 1);
    if (moved) next.splice(target, 0, moved);
    change(next);
  };

  const discard = () => {
    setDraft(toDraft(checklist.items));
    setDirty(false);
    setEditing(false);
    setDiscardOpen(false);
  };

  const save = async () => {
    const items = draft
      .filter((item) => item.title.trim().length > 0)
      .map((item) => ({
        title: item.title.trim(),
        expectedResult: item.expectedResult.trim() ? item.expectedResult.trim() : null,
        testCaseId: item.testCase?.id ?? null,
      }));

    try {
      await replaceItems.mutateAsync({ items });
      toast.success(t('checklists.itemsSaved'));
      setDirty(false);
      setEditing(false);
    } catch (saveError) {
      toast.error(errorText(saveError, 'checklists.itemsSaveFailed'));
    }
  };

  const onDuplicate = async () => {
    try {
      const copy = await duplicate.mutateAsync(checklist.id);
      toast.success(t('checklists.duplicated', { key: copy.checklistKey }));
    } catch (duplicateError) {
      toast.error(errorText(duplicateError, 'checklists.duplicateFailed'));
    }
  };

  const confirmDelete = async () => {
    try {
      await remove.mutateAsync(checklist.id);
      toast.success(t('checklists.deleted'));
      await navigate({ to: '/projects/$projectCode/checklists', params: { projectCode } });
    } catch (deleteError) {
      toast.error(errorText(deleteError, 'checklists.deleteFailed'));
    }
  };

  return (
    <>
      <PageHeader
        title={checklist.title}
        description={checklist.description ?? undefined}
        actions={
          <>
            <Link
              to="/projects/$projectCode/checklists"
              params={{ projectCode }}
              className={buttonClassName('secondary')}
            >
              <ArrowLeft className="size-3.5" />
              {t('cases.backToList')}
            </Link>
            {editable ? (
              <>
                <Button onClick={() => void onDuplicate()} loading={duplicate.isPending}>
                  <Copy className="size-3.5" />
                  {t('common.duplicate')}
                </Button>
                <Button variant="primary" onClick={() => setEditOpen(true)}>
                  <Pencil className="size-3.5" />
                  {t('common.edit')}
                </Button>
              </>
            ) : null}
            {can('checklist:delete') ? (
              <Button
                variant="ghost"
                onClick={() => setDeleteOpen(true)}
                aria-label={t('checklists.deleteAction')}
              >
                <Trash2 className="size-3.5 text-fail" />
              </Button>
            ) : null}
          </>
        }
      />

      <CollapsibleCard title={t('checklists.detailsTitle')} rememberAs="checklistDetails">
        <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2 xl:grid-cols-4">
          <div>
            <dt className="text-[12px] uppercase tracking-wide text-ink-subtle">
              {t('cases.columnKey')}
            </dt>
            <dd className="mt-1">
              <CopyKey path={`/projects/${projectCode}/checklists/${checklist.checklistKey}`}>
                {checklist.checklistKey}
              </CopyKey>
            </dd>
          </div>
          <div>
            <dt className="text-[12px] uppercase tracking-wide text-ink-subtle">
              {t('common.status')}
            </dt>
            <dd className="mt-1">
              <CaseStatusBadge status={checklist.status} />
            </dd>
          </div>
          <div>
            <dt className="text-[12px] uppercase tracking-wide text-ink-subtle">
              {t('labels.title')}
            </dt>
            <dd className="mt-1">
              {checklist.labels.length === 0 ? (
                <span className="text-[13px] text-ink-subtle">{t('common.empty')}</span>
              ) : (
                <span className="flex flex-wrap gap-1">
                  {checklist.labels.map((label) => (
                    <LabelChip key={label.id} label={label} />
                  ))}
                </span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-[12px] uppercase tracking-wide text-ink-subtle">
              {t('common.environment')}
            </dt>
            <dd className="mt-1 text-[13px] text-ink">
              {checklist.environment?.name ?? t('environments.none')}
            </dd>
          </div>
          <div>
            <dt className="text-[12px] uppercase tracking-wide text-ink-subtle">
              {t('common.layer')}
            </dt>
            <dd className="mt-1 text-[13px] text-ink">
              {checklist.layer?.name ?? t('layers.none')}
            </dd>
          </div>
          <div>
            <dt className="text-[12px] uppercase tracking-wide text-ink-subtle">
              {t('common.module')}
            </dt>
            <dd className="mt-1 text-[13px] text-ink">
              {checklist.module?.name ?? t('modules.none')}
            </dd>
          </div>
          <div>
            <dt className="text-[12px] uppercase tracking-wide text-ink-subtle">
              {t('common.author')}
            </dt>
            <dd className="mt-1 text-[13px] text-ink">
              {checklist.createdBy?.name ?? t('common.empty')}
            </dd>
          </div>
          <div>
            <dt className="text-[12px] uppercase tracking-wide text-ink-subtle">
              {t('common.created')}
            </dt>
            <dd className="mt-1 text-[13px] text-ink">{dateTime(checklist.createdAt)}</dd>
          </div>
          <div>
            <dt className="text-[12px] uppercase tracking-wide text-ink-subtle">
              {t('common.updated')}
            </dt>
            <dd className="mt-1 text-[13px] text-ink">{dateTime(checklist.updatedAt)}</dd>
          </div>
        </dl>
      </CollapsibleCard>

      <Card>
        <CardHeader
          title={t('checklists.itemsTitle')}
          action={
            editable ? (
              editing ? (
                // Saving and discarding only exist while editing — reading a
                // checklist should not offer ways to change it.
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => (dirty ? setDiscardOpen(true) : discard())}
                  >
                    {t('checklists.discardConfirm')}
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => void save()}
                    loading={replaceItems.isPending}
                    disabled={!dirty}
                  >
                    <Save className="size-3.5" />
                    {t('checklists.saveItems')}
                  </Button>
                </div>
              ) : (
                <Button size="sm" onClick={() => setEditing(true)}>
                  <Pencil className="size-3.5" />
                  {t('checklists.editItems')}
                </Button>
              )
            ) : null
          }
        />
        <CardBody className="flex flex-col gap-2">
          {draft.length === 0 ? (
            <p className="rounded-md border border-dashed border-border px-3 py-6 text-center text-[13px] text-ink-subtle">
              {editable ? t('checklists.itemsEmptyEditable') : t('checklists.itemsEmptyReadonly')}
            </p>
          ) : editing ? (
            draft.map((item, index) => (
              <div key={index} className="rounded-lg border border-border bg-canvas p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="text-[12px] tabular-nums text-ink-subtle">{index + 1}</span>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => move(index, -1)}
                      disabled={index === 0}
                      aria-label={t('checklists.moveItemUp', { number: index + 1 })}
                    >
                      <ArrowUp className="size-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => move(index, 1)}
                      disabled={index === draft.length - 1}
                      aria-label={t('checklists.moveItemDown', { number: index + 1 })}
                    >
                      <ArrowDown className="size-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => change(draft.filter((_, position) => position !== index))}
                      aria-label={t('checklists.removeItem', { number: index + 1 })}
                      className="text-fail hover:text-fail"
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  <div className="grid gap-3 lg:grid-cols-2">
                    <div className="flex flex-col gap-1.5">
                      <span className="text-[12px] font-medium text-ink">
                        {t('checklists.itemTitleLabel')}
                      </span>
                      <RichTextEditor
                        projectCode={projectCode}
                        value={item.title}
                        onChange={(value) => update(index, { title: value })}
                        placeholder={t('checklists.itemTitle')}
                        aria-label={t('checklists.itemTitleAria', { number: index + 1 })}
                        rows={3}
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <span className="text-[12px] font-medium text-ink">
                        {t('checklists.itemExpectedLabel')}
                      </span>
                      <RichTextEditor
                        projectCode={projectCode}
                        value={item.expectedResult}
                        onChange={(value) => update(index, { expectedResult: value })}
                        placeholder={t('checklists.itemExpected')}
                        aria-label={t('checklists.itemExpectedAria', { number: index + 1 })}
                        rows={3}
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[12px] font-medium text-ink">
                      {t('checklists.itemLinkedCase')}
                    </span>
                    {item.testCase ? (
                      <>
                        <EntityKey className="rounded bg-sunken px-1.5 py-0.5">
                          {item.testCase.caseKey}
                        </EntityKey>
                        <span className="min-w-0 truncate text-[13px] text-ink-muted">
                          {item.testCase.title}
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => update(index, { testCase: null })}
                          aria-label={t('checklists.unlinkCase', { key: item.testCase.caseKey })}
                        >
                          <Link2Off className="size-3.5" />
                        </Button>
                      </>
                    ) : (
                      <Button size="sm" onClick={() => setLinkingIndex(index)}>
                        <Link2 className="size-3.5" />
                        {t('checklists.linkCase')}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th className="w-12">#</Th>
                    <Th>{t('checklists.itemTitleLabel')}</Th>
                    <Th>{t('checklists.itemExpectedLabel')}</Th>
                    <Th className="w-40">{t('checklists.itemLinkedCase')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {draft.map((item, index) => (
                    <tr key={index}>
                      <Td className="align-top tabular-nums text-ink-subtle">{index + 1}</Td>
                      <Td className="align-top">
                        <RichText value={item.title} />
                      </Td>
                      <Td className="align-top">
                        {isBlank(item.expectedResult) ? (
                          <span className="text-ink-subtle">{t('common.empty')}</span>
                        ) : (
                          <RichText value={item.expectedResult} />
                        )}
                      </Td>
                      <Td className="align-top">
                        {item.testCase ? (
                          <Link
                            to="/projects/$projectCode/test-cases/$caseKey"
                            params={{ projectCode, caseKey: item.testCase.caseKey }}
                            className="flex w-fit items-center gap-1.5 text-[12px] text-accent-fg hover:underline"
                          >
                            <Link2 className="size-3" />
                            <EntityKey className="text-accent-fg">
                              {item.testCase.caseKey}
                            </EntityKey>
                          </Link>
                        ) : (
                          <span className="text-ink-subtle">{t('common.empty')}</span>
                        )}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          )}

          {editing ? (
            <div>
              <Button
                onClick={() => change([...draft, { title: '', expectedResult: '', testCase: null }])}
              >
                <Plus className="size-3.5" />
                {t('checklists.addItem')}
              </Button>
            </div>
          ) : null}
        </CardBody>
      </Card>

      <ChecklistFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        projectCode={projectCode}
        checklist={checklist}
      />

      <LinkCaseDialog
        open={linkingIndex !== null}
        onOpenChange={(open) => {
          if (!open) setLinkingIndex(null);
        }}
        projectCode={projectCode}
        onPick={(testCase) => {
          if (linkingIndex !== null) update(linkingIndex, { testCase });
          setLinkingIndex(null);
        }}
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t('checklists.deleteTitle', { title: checklist.title })}
        description={t('checklists.deleteHint')}
        confirmLabel={t('checklists.deleteAction')}
        loading={remove.isPending}
        onConfirm={() => void confirmDelete()}
      />

      <ConfirmDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title={t('checklists.discardTitle')}
        description={t('checklists.discardHint')}
        confirmLabel={t('checklists.discardConfirm')}
        onConfirm={discard}
      />
    </>
  );
};
